import {
  Box,
  Button,
  Cropper,
  FormInput,
  Separator,
  Slider,
  Tooltip
} from "@prismicio/editor-ui"
import { useState } from "react"

import type { CropperImage } from "../../../core/imageUtils"
import { type CropArea, type CroppedImage, type Point } from "./croppedImage"

interface Props {
  selectedImage: CropperImage
  imageCrop: CroppedImage
  setImageCrop: (imageCrop: CroppedImage) => void
}

export function CropperControls(props: Props) {
  const { selectedImage, imageCrop, setImageCrop } = props

  const { width, height, zoom, cropHeight, cropWidth } = imageCrop
  const contentView = selectedImage.contentView

  const initialCropArea = {
    x: imageCrop.x,
    y: imageCrop.y,
    width: cropWidth,
    height: cropHeight
  }

  // Internal state to display the crop area
  const [crop, setCrop] = useState<Point>()

  const imageUrl = contentView?.origin.url ?? ""
  const canResize = !(
    selectedImage?.thumbnail.height && selectedImage?.thumbnail.width
  )

  function onDimensionChange(axis: "width" | "height", value: string) {
    const intValue = parseInt(value)
    if (isNaN(intValue) || intValue <= 0 || !canResize) return

    setImageCrop({
      ...imageCrop,
      [axis]: intValue
    })
  }

  function onCropAreaChange(crop: CropArea) {
    setImageCrop({
      ...imageCrop,
      cropWidth: crop.width,
      cropHeight: crop.height,
      x: crop.x,
      y: crop.y
    })
  }

  function onZoomChange(zoom: number) {
    setImageCrop({
      ...imageCrop,
      zoom
    })
  }

  function centerCrop() {
    setCrop(defaultCrop)
    setImageCrop({
      ...imageCrop,
      ...defaultCrop
    })
  }

  function cover() {
    if (!contentView) return

    setCrop(defaultCrop)
    setImageCrop({
      ...imageCrop,
      ...defaultCrop,
      zoom: 1,
      cropWidth: contentView.width,
      cropHeight: contentView.height
    })
  }

  return (
    <Box
      padding={{ inline: 24, block: 16 }}
      flexDirection="column"
      gap={16}
      height="100%"
      overflowY="auto"
      alignItems="center"
    >
      <Box gap={8} height={32} alignItems="flex-start" width="100%">
        <Tooltip content="Center">
          <Button
            color="grey"
            size="medium"
            startIcon="centerFocusWeak"
            onClick={centerCrop}
          />
        </Tooltip>
        <Tooltip content="Cover">
          <Button
            color="grey"
            size="medium"
            startIcon="zoomOutMap"
            onClick={cover}
          />
        </Tooltip>
        <Separator orientation="vertical" />
        <FormInput
          prefix="W"
          prefixTooltip="Width"
          placeholder="width"
          type="number"
          size="medium"
          value={String(width)}
          onValueChange={(value) => onDimensionChange("width", value)}
          sx={{ width: 100 }}
          disabled={!canResize}
        />
        <FormInput
          prefix="H"
          prefixTooltip="Height"
          placeholder="height"
          type="number"
          size="medium"
          value={String(height)}
          onValueChange={(value) => onDimensionChange("height", value)}
          sx={{ width: 100 }}
          disabled={!canResize}
        />
      </Box>
      <Cropper
        image={imageUrl}
        aspect={width / height}
        crop={crop ?? { x: 0, y: 0 }}
        onCropChange={setCrop}
        zoom={zoom}
        onZoomChange={onZoomChange}
        onCropAreaChange={(_, cropArea) => onCropAreaChange(cropArea)}
        initialCroppedAreaPixels={initialCropArea}
      />
      <Box width="100%" maxWidth={320}>
        <Slider
          min={1}
          max={3}
          step={0.01}
          value={[zoom]}
          onValueChange={([newZoom]) => onZoomChange(newZoom ?? 1)}
        />
      </Box>
    </Box>
  )
}

const defaultCrop = { x: 0, y: 0 }
